import type { CSSProperties } from 'react';
import type { Branding } from './types';

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance([r, g, b]: [number, number, number]) {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrastRatio(a: string, b: string): number {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return 1;
  const la = luminance(ra);
  const lb = luminance(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Tenant primary colour override for the client portal. Only applied when it keeps
 * interactive elements readable: the colour must reach 3:1 against the page surface, and
 * button text flips between white and near-black for at least 4.5:1.
 */
export function readableBrandColor(color: string, theme: 'light' | 'dark'): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const surface = theme === 'dark' ? '#292a2d' : '#ffffff';
  for (let step = 0; step <= 100; step++) {
    const amount = step / 100;
    const adjusted = '#' + rgb.map(c => Math.round(c + ((theme === 'dark' ? 255 : 0) - c) * amount).toString(16).padStart(2, '0')).join('');
    if (contrastRatio(adjusted, surface) >= 4.5) return adjusted;
  }
  return color;
}

export function brandingStyle(branding: Branding | undefined, theme: 'light' | 'dark'): CSSProperties | undefined {
  const color = branding?.primaryColor || branding?.accentColor ? readableBrandColor(branding.primaryColor || '#4285F4', theme) : undefined;
  if (!color || !hexToRgb(color)) return undefined;
  const surface = theme === 'dark' ? '#292a2d' : '#ffffff';
  if (contrastRatio(color, surface) < 3) return undefined;
  const onColor = contrastRatio(color, '#ffffff') >= 4.5 ? '#ffffff' : '#202124';
  if (contrastRatio(color, onColor) < 4.5) return undefined;
  const textOk = contrastRatio(color, surface) >= 4.5;
  return {
    ['--brand-accent' as string]: color,
    ['--brand-accent-contrast' as string]: onColor,
    ['--color-primary' as string]: color,
    ['--color-primary-hover' as string]: `color-mix(in srgb, ${color} 88%, ${theme === 'dark' ? 'white' : 'black'})`,
    ['--color-primary-active' as string]: `color-mix(in srgb, ${color} 78%, ${theme === 'dark' ? 'white' : 'black'})`,
    ['--color-on-primary' as string]: onColor,
    ['--color-primary-subtle' as string]: `color-mix(in srgb, ${color} 12%, transparent)`,
    ['--color-primary-soft' as string]: `color-mix(in srgb, ${color} 12%, transparent)`,
    ['--color-selected' as string]: `color-mix(in srgb, ${color} 12%, transparent)`,
    ['--app-blue-wash' as string]: `color-mix(in srgb, ${color} 12%, transparent)`,
    ['--app-chart' as string]: color,
    ['--color-focus' as string]: color,
    ...(branding?.accentColor && hexToRgb(branding.accentColor) ? { ['--brand-secondary' as string]: readableBrandColor(branding.accentColor, theme), ['--color-accent' as string]: readableBrandColor(branding.accentColor, theme) } : {}),
    ...(textOk ? { ['--color-primary-text' as string]: color, ['--color-link' as string]: color } : {}),
  };
}
